import { MutableRefObject, useCallback } from 'react';
import Handsontable from 'handsontable/base';
import useRefCallback from '../../../utils/react/useRefCallback';
import { isHotInstanceAvailable } from '../utils/hotInstanceUtils/hotTableInstanceValidationUtils';
import { useDispatch } from 'react-redux';
import { startEditingElement } from '../../selection/selectionActions';
import { getTableCellEditorId, getTableEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';
import {
    TABLE_CELL_EDITOR_KEY,
    TABLE_GRID_EDITOR_KEY,
    TABLE_TITLE_EDITOR_KEY,
} from '../../../../common/table/tableConstants';
import { CAPTION_EDITOR_KEY } from '../../../components/caption/captionConstants';
import { getSpecificEditorId } from '../../utils/elementEditorUtils';

/**
 * Hooks used by the Milanote table editing plugins (MilanoteEditingPlugin & MilanoteCellEditor).
 *
 * NOTE: `useStableCallback` allows us to pass these functions into the HotTable config (which is only
 * initialised once) without worrying about the function identity changing on each render.
 */
export const useTableEditingHandlers = ({
    hotTableInstanceRef,
    showTitle,
    showCaption,
    elementId,
    isCurrentlyEditingTableCell,
    setCurrentlyEditingCellEditorCoords,
}: {
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    showTitle: boolean;
    showCaption: boolean;
    elementId: string;
    isCurrentlyEditingTableCell: boolean;
    setCurrentlyEditingCellEditorCoords: (cellCoords: { row: number; col: number } | null) => void;
}) => {
    const dispatch = useDispatch();

    const dispatchStartEditingTitle = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_TITLE_EDITOR_KEY,
                    editorId: getTableEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingCaption = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: CAPTION_EDITOR_KEY,
                    editorId: getSpecificEditorId(elementId, CAPTION_EDITOR_KEY),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingTableCell = useCallback(
        (editorFocusRect) =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_CELL_EDITOR_KEY,
                    editorId: getTableCellEditorId(elementId),
                    editorFocusRect,
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingTableGrid = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_GRID_EDITOR_KEY,
                    editorId: getTableGridEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    /**
     * Edits the table title.
     */
    const jumpToTitle = useRefCallback(() => {
        showTitle && requestAnimationFrame(() => dispatchStartEditingTitle());
    });

    /**
     * Edits the table caption.
     */
    const jumpToCaption = useRefCallback(() => {
        showCaption && requestAnimationFrame(() => dispatchStartEditingCaption());
    });

    /**
     * Starts editing the cell editor.
     */
    const startEditingCellEditor = useRefCallback((cellCoords) => {
        setCurrentlyEditingCellEditorCoords(cellCoords);

        requestAnimationFrame(() => {
            const hotInstance = hotTableInstanceRef.current;
            if (!isHotInstanceAvailable(hotInstance)) return;

            if (!isCurrentlyEditingTableCell) {
                // TODO: Once table editor is updated to TipTap, we can use the specific caret client coord
                //       instead of the cell client coord
                const cellRect = hotInstance.getCell(cellCoords.row, cellCoords.col)?.getBoundingClientRect();

                dispatchStartEditingTableCell(cellRect);
            }
        });
    });

    /**
     * Finishes editing the cell editor.
     */
    const finishEditingCellEditor = useRefCallback(() => {
        setCurrentlyEditingCellEditorCoords(null);

        requestAnimationFrame(() => {
            if (isCurrentlyEditingTableCell) dispatchStartEditingTableGrid();
        });
    });

    return {
        jumpToCaption,
        jumpToTitle,
        startEditingCellEditor,
        finishEditingCellEditor,
    };
};
